import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";

import DeleteModal from "../../../../components/DeleteModal";
import { isJeepRallyAdmin } from "../../Categories/utils/category.utils";
import {
  useGetAllRoutes,
  useDeleteRouteMutation,
} from "../../../../../api/jeep-rally/routes/routes.hooks";
import {
  filterRoutes,
  getRouteApiErrorMessage,
  normalizeRoutes,
} from "../utils/route.utils";
import RoutesHeader from "./_components/RoutesHeader";
import RoutesTable from "./_components/RoutesTable";

const RoutesListPage = () => {
  const history = useHistory();
  const canManage = isJeepRallyAdmin();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const { data, isLoading, isError, error } = useGetAllRoutes({
    status: statusFilter,
  });

  const routes = useMemo(() => normalizeRoutes(data), [data]);
  const filteredRoutes = useMemo(
    () => filterRoutes(routes, searchTerm),
    [routes, searchTerm]
  );

  const {
    mutate: deleteRoute,
    isLoading: isDeleting,
    isPending: isDeletePending,
  } = useDeleteRouteMutation();
  const deleting = isDeleting || isDeletePending;

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setSelectedRoute(null);
    setDeleteError("");
  };

  const openDeleteModal = (route) => {
    setSelectedRoute(route);
    setDeleteError("");
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    const routeId = selectedRoute?._id || selectedRoute?.id;
    if (!routeId || !canManage) return;

    setDeleteError("");
    deleteRoute(
      { routeId },
      {
        onSuccess: () => closeDeleteModal(),
        onError: (deleteErr) => {
          setDeleteError(
            getRouteApiErrorMessage(
              deleteErr,
              "Failed to delete route. Please try again."
            )
          );
        },
      }
    );
  };

  const handleEdit = (route) => {
    const routeId = route?._id || route?.id;
    if (!routeId) return;
    history.push(`/jeep-rally/routes/edit/${routeId}`, { route });
  };

  const handleView = (route) => {
    const routeId = route?._id || route?.id;
    if (!routeId) return;
    history.push(`/jeep-rally/routes/view/${routeId}`, { route });
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load routes.{" "}
            {getRouteApiErrorMessage(error, "Please try again.")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <RoutesHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          total={filteredRoutes.length}
          canManage={canManage}
          onAddRoute={() => history.push("/jeep-rally/routes/create")}
        />

        <div className="card-body">
          {filteredRoutes.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No routes found</p>
            </div>
          ) : (
            <RoutesTable
              routes={filteredRoutes}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={openDeleteModal}
              isDeleting={deleting}
              canManage={canManage}
            />
          )}

          {!canManage && (
            <Alert variant="warning" className="mt-3 mb-0">
              Admin access is required to create/edit/delete routes.
            </Alert>
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Route"
        message={`Are you sure you want to delete "${
          selectedRoute?.title || "this route"
        }"? This will also permanently delete all linked stages, checkpoints, and stage results.${
          deleteError ? ` ${deleteError}` : ""
        }`}
        confirmText="Delete Route"
        isLoading={deleting}
      />
    </div>
  );
};

export default RoutesListPage;
